import type { BoxlessDropoffPricing } from "@redotech/admin-sdk/rpc/schema/integrations/boxless-dropoff-fees";
import { DropoffPricingMode } from "@redotech/congo-enums/dropoff-pricing-mode";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { DropoffProviderType } from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import classNames from "classnames";
import { memo, useState } from "react";
import * as teamCss from "../../team.module.css";
import {
  boxlessDropoffsForm,
  BoxlessDropoffsSettings,
  BoxlessDropoffsValue,
  MarkupType,
} from "./boxless-dropoffs-settings";
import { useBoxlessDropoffs } from "./use-boxless-dropoffs";

export const BoxlessDropoffsPage = memo(function BoxlessDropoffsPage() {
  const { teamId, initial, loaded, error, updatePricing, refresh } =
    useBoxlessDropoffs();

  if (error) {
    return (
      <Flex>
        <Text textColor="error">{`Something went wrong loading Boxless Drop-offs settings: ${error instanceof Error ? error.message : String(error)}`}</Text>
      </Flex>
    );
  }

  if (!loaded) {
    return null;
  }

  // The form owns its baseline, so it belongs to the team it was loaded for.
  return (
    <BoxlessDropoffsForm
      initial={initial}
      key={teamId}
      refresh={refresh}
      updatePricing={updatePricing}
    />
  );
});

const BoxlessDropoffsForm = memo(function BoxlessDropoffsForm({
  initial,
  updatePricing,
  refresh,
}: {
  initial: BoxlessDropoffsValue;
  updatePricing(
    provider: DropoffProviderType,
    pricing: BoxlessDropoffPricing,
    signal: AbortSignal,
  ): Promise<void>;
  refresh(): void;
}) {
  // The saved value becomes the new baseline, so Save clears on save rather
  // than waiting for the reload to land.
  const [baseline, setBaseline] = useState(initial);
  const formInput = useInput(boxlessDropoffsForm, baseline);

  const save: PageFormSave = useHandler(async (signal) => {
    for (const feeInput of formInput.inputs) {
      if (!feeInput.changed) {
        continue;
      }
      await updatePricing(
        feeInput.value.provider,
        toPricing(feeInput.value),
        signal,
      );
    }
    // Snap number strings to the canonical form the reload returns, so the form is clean post-save.
    const saved = formInput.value.map((entry) => ({
      ...entry,
      amount: String(Number(entry.amount)),
      markupAmount: String(Number(entry.markupAmount)),
      markupPercent: String(Number(entry.markupPercent)),
    }));
    formInput.setValue(saved);
    setBaseline(saved);
    refresh();
  });

  return (
    <div className={teamCss.container}>
      <PageForm initial={baseline} input={formInput} save={save}>
        <div className={classNames(teamCss.mainForm, gridCss.grid)}>
          <div className={gridCss.span12}>
            <BoxlessDropoffsSettings input={formInput} />
          </div>
        </div>
      </PageForm>
    </div>
  );
});

function toPricing(value: BoxlessDropoffsValue[number]): BoxlessDropoffPricing {
  switch (value.mode) {
    case DropoffPricingMode.Default:
      return { mode: value.mode };
    case DropoffPricingMode.Fixed:
      return {
        mode: value.mode,
        amount: Number(value.amount),
        currency: value.currency,
      };
    case DropoffPricingMode.Markup:
      // Only the chosen field is sent, so the row carries no unused zero.
      return value.markupType === MarkupType.Flat
        ? { mode: value.mode, amount: Number(value.markupAmount) }
        : { mode: value.mode, percent: Number(value.markupPercent) };
    default:
      assertNever(value.mode);
  }
}
